// The APIs we call at a vendor and the webhooks a vendor calls on us (plan v2 §3.2): one row each, with its counters
// (today, 7 days, 30 days, errors, average time) and its settings (path, timeout, retries, calls per minute, on / off).
// A call is shown with its time, result, time taken and campaign only: never a mobile, a name or a body.
import { z } from 'zod'
import { Id, IsoDate, IsoDateTime } from './common'

const count = z.number().int().min(0)

/** channels: Exotel, Pingbix, Meta; lenders: the Lender Hub; firstfund: our own services (registry, CMP). */
export const VendorKind = z.enum(['channels', 'lenders', 'firstfund'])
export type VendorKind = z.infer<typeof VendorKind>

/** call = we call the vendor; webhook = the vendor calls us (call status, digit pressed, delivered / read / reply). */
export const ApiDirection = z.enum(['call', 'webhook'])
export type ApiDirection = z.infer<typeof ApiDirection>

/** errors_rising: today's error share is well above the 7 days before (rules/api-health.ts). */
export const ApiHealth = z.enum(['ok', 'errors_rising', 'switched_off'])
export type ApiHealth = z.infer<typeof ApiHealth>

/** The counters every API row and every Lender Hub route carries. */
export const ApiCounters = z.object({
  calls_today: count,
  calls_7d: count.describe('today and the 6 days before'),
  calls_30d: count,
  errors_today: count,
  errors_30d: count,
  avg_ms: count.describe('average time to answer over 30 days, in milliseconds'),
  last_call_at: IsoDateTime.nullable(),
})
export type ApiCounters = z.infer<typeof ApiCounters>

export const VendorApiRow = ApiCounters.extend({
  id: Id,
  vendor_id: Id,
  name: z.string(),
  explanation: z.string().describe('what it does, in plain words'),
  direction: ApiDirection,
  method: z.string().describe('GET, POST, …'),
  path: z
    .string()
    .describe("the path after the vendor's base address; for a webhook, the path on our address"),
  used_by: z.array(z.string()).describe('the workflow nodes, pages or steps that use it'),
  status: ApiHealth,
})
export type VendorApiRow = z.infer<typeof VendorApiRow>

/** What can change without code (plan v2 §3.3). A webhook only uses `enabled`; the other values stay as they are. */
export const ApiSettings = z.object({
  path: z
    .string()
    .trim()
    .regex(/^\/\S*$/, 'A path starts with / and has no spaces'),
  timeout_ms: z
    .number({ error: 'A number of milliseconds' })
    .int()
    .min(500, 'At least 500 ms')
    .max(30000, 'At most 30000 ms'),
  retries: z.number({ error: 'A number from 0 to 3' }).int().min(0, '0 to 3').max(3, '0 to 3'),
  per_minute: z.number({ error: 'A number of calls' }).int().min(1, 'At least 1').max(600, 'At most 600'),
  enabled: z.boolean(),
})
export type ApiSettings = z.infer<typeof ApiSettings>

export const ApiDay = z.object({ date: IsoDate, calls: count, errors: count })
export type ApiDay = z.infer<typeof ApiDay>

/** One call, without anything personal: no mobile, no name, no request or answer body (only a refusal's masked message). */
export const ApiCall = z.object({
  at: IsoDateTime,
  ok: z.boolean(),
  code: z.string().describe('the HTTP status, or "timeout"'),
  ms: count,
  campaign_id: Id.nullable(),
  campaign_name: z.string().nullable().describe('null when no campaign made the call (a page, a sync)'),
  error: z
    .string()
    .optional()
    .describe("a refused call: the vendor's error message (digits and email addresses masked); '' when it went through"),
})
export type ApiCall = z.infer<typeof ApiCall>

/** The vendor as an API's page needs it. */
export const VendorRef = z.object({
  id: Id,
  name: z.string(),
  kind: VendorKind,
  base_url: z.string(),
  live: z.boolean().describe('real traffic: a change waits for a second person'),
  read_only: z.boolean(),
  onboarded: z.boolean(),
})
export type VendorRef = z.infer<typeof VendorRef>

export const VendorApiDetail = VendorApiRow.extend({
  when_called: z.string().describe('which step, page or webhook calls it, and how often'),
  vendor: VendorRef,
  settings: ApiSettings,
  days: z.array(ApiDay).describe('the last 30 days, oldest first; the last one is today'),
  last_calls: z.array(ApiCall).max(50).describe('newest first'),
})
export type VendorApiDetail = z.infer<typeof VendorApiDetail>

export interface VendorApisApi {
  getVendorApi(apiId: string): Promise<VendorApiDetail>
  /** at once (an outage); switching back on goes through a change: Test, then a second person on a live vendor */
  switchOffApi(apiId: string): Promise<VendorApiRow>
}
